import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import moment from "moment";
import { toast } from "react-toastify";

import BreadCrumbs from "../../../components/BreadCrumbs";
import PageLoaderComponent from "../../../components/page-loader";
import { authApi } from "../../../api-calls/user-requests";
import { EXPENSES, EXPENSE_APPROVALS } from "../../../api-calls/api-urls";
import { getChipColor, stringAvatar } from "../../../utilities/commonFunctions";
import { getLanguagedataFunction } from "../../../utilities/language";
import { setHeaderName } from "../../../Redux/actions/user-actions";
import {
  EXPENSE_STATUSES,
  EXPENSE_STATUS_LABELS,
  EXPENSE_TYPE_META,
  fetchPendingExpenseCount,
  formatAmount,
  isPdfBill,
  resolveBillUrl,
} from "../../../utilities/expenses";
import RejectExpenseDialog from "./RejectExpenseDialog";
import classes from "./expenseApprovals.module.css";

const PAGE_SIZE = 20;
// Reviewed tabs open on the last month; Pending always shows everything waiting.
const REVIEWED_RANGE_DAYS = 30;
const HIGHLIGHT_MS = 3000;

const ExpenseApprovals = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const ln = useSelector((state) => state.allUsers.language_json);
  const teamMembersList = useSelector(
    (state) => state.allUsers.teamMembersList
  );

  const [status, setStatus] = useState("submitted");
  const [memberId, setMemberId] = useState("");
  const [fromDate, setFromDate] = useState(dayjs().subtract(REVIEWED_RANGE_DAYS, "day"));
  const [toDate, setToDate] = useState(dayjs());

  const [rows, setRows] = useState([]);
  // Which tab the rows on screen belong to — they lag `status` while loading.
  const [rowsStatus, setRowsStatus] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [preview, setPreview] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const highlightRef = useRef(null);
  // MUI only re-measures the tab indicator on window resize; switching tabs
  // shows/hides the date pickers and changes the count labels, which shifts
  // the tabs without one, leaving the underline under the old tab.
  const tabsActions = useRef(null);
  useEffect(() => {
    const id = setTimeout(() => tabsActions.current?.updateIndicator(), 50);
    return () => clearTimeout(id);
  }, [status, counts]);

  useEffect(() => {
    getLanguagedataFunction(dispatch);
  }, [!ln]);

  // Header.js labels pages from ln[<path>]; this page has no language key.
  useEffect(() => {
    dispatch(setHeaderName(ln?.expense_approvals || "Expense Approvals"));
  }, [ln]);

  const memberOptions = useMemo(() => {
    const seen = new Set();
    return (teamMembersList || [])
      .map((m) => ({
        id: String(m?.user_id || m?.user?._id || ""),
        name: m?.user?.name || m?.name || "",
      }))
      .filter((m) => m.id && !seen.has(m.id) && seen.add(m.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [teamMembersList]);

  const load = useCallback(
    async (nextPage = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          status,
          page: String(nextPage),
          limit: String(PAGE_SIZE),
        });
        if (memberId) params.append("user_id", memberId);
        if (status !== "submitted") {
          if (fromDate) params.append("start_date", fromDate.format("YYYY-MM-DD"));
          if (toDate) params.append("end_date", toDate.format("YYYY-MM-DD"));
        }
        const { data } = await authApi.get(`${EXPENSE_APPROVALS}?${params}`);
        const result = data?.data || {};
        setRows((prev) =>
          nextPage === 1 ? result.list || [] : [...prev, ...(result.list || [])]
        );
        setRowsStatus(status);
        setPage(nextPage);
        setHasMore(!!result.hasMore);
        setCounts(result.counts || null);
        setError("");
      } catch (err) {
        setError(err?.response?.data?.message || "Could not load expenses");
      } finally {
        setLoading(false);
      }
    },
    [status, memberId, fromDate, toDate]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  // ?expense=<id> (from a notification): switch to Pending and flash that row.
  const deepLinkId = searchParams.get("expense");
  useEffect(() => {
    if (!deepLinkId || loading) return;
    if (status !== "submitted") {
      setRows([]);
      setStatus("submitted");
      return;
    }
    // Wait for the Pending rows themselves, not the previous tab's.
    if (rowsStatus !== "submitted") return;
    if (rows.some((r) => r._id === deepLinkId)) {
      setHighlightId(deepLinkId);
      setTimeout(() => highlightRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 100);
      setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    } else if (hasMore) {
      load(page + 1);
      return;
    } else {
      toast.info("That expense has already been reviewed.");
    }
    searchParams.delete("expense");
    setSearchParams(searchParams, { replace: true });
  }, [deepLinkId, loading, rows, rowsStatus, status, hasMore]);

  const afterReview = (expenseId) => {
    setRows((prev) => prev.filter((r) => r._id !== expenseId));
    setCounts((prev) =>
      prev ? { ...prev, submitted: Math.max((prev.submitted || 1) - 1, 0) } : prev
    );
    fetchPendingExpenseCount(dispatch);
  };

  const handleError = (err, expenseId) => {
    if (err?.response?.status === 409) {
      toast.info(err.response.data?.message || "This expense was already reviewed.");
      afterReview(expenseId);
      load(1);
      return;
    }
    toast.error(err?.response?.data?.message || "Could not update the expense");
  };

  const approve = async (row) => {
    setBusyId(row._id);
    try {
      await authApi.put(`${EXPENSES}/${row._id}/approve`, {});
      toast.success(`Approved ₹${formatAmount(row.amount)} for ${row.user_id?.name || "member"}`);
      afterReview(row._id);
      load(1);
    } catch (err) {
      handleError(err, row._id);
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async (reason) => {
    const row = rejecting;
    if (!row) return;
    setBusyId(row._id);
    try {
      await authApi.put(`${EXPENSES}/${row._id}/reject`, { reason });
      toast.success("Expense rejected");
      setRejecting(null);
      afterReview(row._id);
      load(1);
    } catch (err) {
      if (err?.response?.status === 409) setRejecting(null);
      handleError(err, row._id);
    } finally {
      setBusyId(null);
    }
  };

  const changeTab = (_e, next) => {
    setRows([]);
    setStatus(next);
  };

  const renderBill = (row) => {
    const url = resolveBillUrl(row.bill);
    if (!url) return <Typography color="text.disabled">—</Typography>;
    if (isPdfBill(row.bill)) {
      return (
        <IconButton size="small" href={url} target="_blank" rel="noopener noreferrer">
          <PictureAsPdfOutlinedIcon color="error" />
        </IconButton>
      );
    }
    return (
      <img
        src={url}
        alt="Bill"
        className={classes.billThumb}
        onClick={() => setPreview(url)}
      />
    );
  };

  const renderOutcome = (row) => (
    <Box>
      <Chip
        label={EXPENSE_STATUS_LABELS[row.status] || row.status}
        sx={getChipColor(row.status)}
        size="small"
      />
      <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
        {row.reviewed_by?.name ? `by ${row.reviewed_by.name}` : ""}
        {row.reviewed_at ? ` · ${moment(row.reviewed_at).format("DD MMM, h:mm A")}` : ""}
      </Typography>
      {row.status === "rejected" && row.rejection_reason ? (
        <Typography variant="caption" display="block" className={classes.reason}>
          {row.rejection_reason}
        </Typography>
      ) : null}
    </Box>
  );

  if (!ln) return null;

  const tabLabel = (key) =>
    `${EXPENSE_STATUS_LABELS[key]}${counts ? ` (${counts[key] ?? 0})` : ""}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {loading && rows.length === 0 ? <PageLoaderComponent /> : null}
      <BreadCrumbs />
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box className={classes.toolbar}>
          <Tabs value={status} onChange={changeTab} variant="scrollable" action={tabsActions}>
            {EXPENSE_STATUSES.map((key) => (
              <Tab key={key} value={key} label={tabLabel(key)} sx={{ textTransform: "none", fontWeight: 600 }} />
            ))}
          </Tabs>

          <Box className={classes.filters}>
            <TextField
              select
              size="small"
              label="Member"
              value={memberId}
              onChange={(e) => {
                setRows([]);
                setMemberId(e.target.value);
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All members</MenuItem>
              {memberOptions.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name || "Unnamed"}
                </MenuItem>
              ))}
            </TextField>
            {status !== "submitted" ? (
              <>
                <DesktopDatePicker
                  label="From"
                  value={fromDate}
                  onChange={(v) => v && setFromDate(v)}
                  maxDate={toDate}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: "small" } }}
                />
                <DesktopDatePicker
                  label="To"
                  value={toDate}
                  onChange={(v) => v && setToDate(v)}
                  minDate={fromDate}
                  maxDate={dayjs()}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: "small" } }}
                />
              </>
            ) : null}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {error ? (
          <Typography color="error" align="center" py={2}>
            {error}
          </Typography>
        ) : null}
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead className={classes.thead}>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Description / Task</TableCell>
                <TableCell>Bill</TableCell>
                <TableCell>{status === "submitted" ? "Actions" : "Status"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row) => {
                  const typeMeta = EXPENSE_TYPE_META[row.type] || EXPENSE_TYPE_META.misc;
                  const name = row.user_id?.name || "Team member";
                  const busy = busyId === row._id;
                  const highlighted = highlightId === row._id;
                  return (
                    <TableRow
                      key={row._id}
                      ref={highlighted ? highlightRef : null}
                      className={`${classes.tbody} ${highlighted ? classes.highlight : ""}`}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap="8px">
                          <Avatar {...stringAvatar(name)} sx={{ width: 30, height: 30, fontSize: "12px" }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {name}
                            </Typography>
                            {row.user_id?.phone ? (
                              <Typography variant="caption" color="text.secondary">
                                {row.user_id.phone}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {moment(row.expense_date).format("DD MMM YYYY")}
                      </TableCell>
                      <TableCell>
                        <span
                          className={classes.typePill}
                          style={{ background: `${typeMeta.color}22`, color: typeMeta.color }}
                        >
                          {typeMeta.label}
                        </span>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                        ₹{formatAmount(row.amount)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        {row.description ? (
                          <Typography variant="body2">{row.description}</Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary">
                          {row.location_title || "No related task"}
                        </Typography>
                      </TableCell>
                      <TableCell>{renderBill(row)}</TableCell>
                      <TableCell sx={{ minWidth: 190 }}>
                        {row.status === "submitted" ? (
                          <Box display="flex" gap="8px">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={busy}
                              onClick={() => approve(row)}
                              sx={{ textTransform: "none" }}
                            >
                              {busy && !rejecting ? <CircularProgress size={16} color="inherit" /> : "Approve"}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={busy}
                              onClick={() => setRejecting(row)}
                              sx={{ textTransform: "none" }}
                            >
                              Reject
                            </Button>
                          </Box>
                        ) : (
                          renderOutcome(row)
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    {loading
                      ? "Loading…"
                      : status === "submitted"
                      ? "All caught up — no expenses waiting for approval."
                      : "No expenses in this period."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {hasMore ? (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="outlined" disabled={loading} onClick={() => load(page + 1)}>
              {loading ? "Loading…" : "Load more"}
            </Button>
          </Box>
        ) : null}
      </Paper>

      <RejectExpenseDialog
        expense={rejecting}
        submitting={!!rejecting && busyId === rejecting._id}
        onCancel={() => setRejecting(null)}
        onConfirm={confirmReject}
      />

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md">
        <IconButton onClick={() => setPreview(null)} className={classes.previewClose}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 1 }}>
          {preview ? <img src={preview} alt="Bill" className={classes.previewImg} /> : null}
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ExpenseApprovals;
